/**
 * My Bookings Component - Week Calendar View
 * Shows user's upcoming bookings in a weekly calendar format
 */

import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';
import {
  format,
  startOfWeek,
  endOfWeek,
  addWeeks,
  eachDayOfInterval,
  isSameDay
} from 'date-fns';
import { de } from 'date-fns/locale';
import './MyBookings.css';

// Normalize time to "HH:MM" so "22:00" and "22:00:00" behave consistently
const toHHMM = (t) => {
  if (!t) return '';
  return String(t).slice(0, 5);
};

// Personen einer Buchung; Altbestand ohne Wert zaehlt als 1
const personsOf = (booking) => parseInt(booking.Persons, 10) || 1;
const personsLabel = (n) => (n === 1 ? '1 Person' : `${n} Personen`);

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentWeek, setCurrentWeek] = useState(new Date());

  useEffect(() => {
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getBookings();
      setBookings(response.data || []);
      setError('');
    } catch (err) {
      console.error('MyBookings - Error loading bookings:', err);
      setError('Fehler beim Laden der Buchungen');
    } finally {
      setLoading(false);
    }
  };

  // Buchungen nach Uhrzeit gruppieren
  const groupBookingsByTime = (dayBookings) => {
    const groups = {};
    dayBookings.forEach((booking) => {
      const time = toHHMM(booking['Start Time']);
      if (!groups[time]) groups[time] = [];
      groups[time].push(booking);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  };

  // Alle Buchungen einer Gruppe stornieren
  const handleCancelGroup = async (bookingsAtTime) => {
    const persons = bookingsAtTime.reduce((sum, b) => sum + personsOf(b), 0);
    if (!window.confirm(`${cancelQuestion(bookingsAtTime[0])} für ${personsLabel(persons)} stornieren?`)) return;

    try {
      await Promise.all(bookingsAtTime.map((b) => bookingAPI.cancelBooking(b.id)));
      loadBookings();
    } catch (err) {
      alert(err.response?.data?.error || 'Stornierung fehlgeschlagen');
    }
  };

  // Wochenstart (Montag) und Ende berechnen
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Buchungen für einen Tag filtern (nur zukünftige)
  const getBookingsForDay = (day) => {
    const now = new Date();

    return bookings.filter((booking) => {
      const bookingDateOnly = booking.Date?.split('T')[0] || booking.Date; // "YYYY-MM-DD"
      const bookingTime = toHHMM(booking['Start Time']);

      // Build a valid datetime string always: YYYY-MM-DDTHH:MM:00
      const bookingDateTime = new Date(`${bookingDateOnly}T${bookingTime}:00`);

      return (
        isSameDay(new Date(bookingDateOnly), day) &&
        String(booking.Status || '').toLowerCase() === 'confirmed' &&
        bookingDateTime >= now
      );
    });
  };

  // Bevorstehende Buchungen (sortiert nach Datum/Zeit)
  const upcomingBookings = bookings
    .filter((b) => {
      const bookingDateOnly = b.Date?.split('T')[0] || b.Date;
      const bookingTime = toHHMM(b['Start Time']);
      const bookingDateTime = new Date(`${bookingDateOnly}T${bookingTime}:00`);
      return String(b.Status || '').toLowerCase() === 'confirmed' && bookingDateTime >= new Date();
    })
    .sort((a, b) => {
      const dateA = new Date(`${(a.Date?.split('T')[0] || a.Date)}T${toHHMM(a['Start Time'])}:00`);
      const dateB = new Date(`${(b.Date?.split('T')[0] || b.Date)}T${toHHMM(b['Start Time'])}:00`);
      return dateA - dateB;
    });

  const upcomingCount = upcomingBookings.length;

  const [cancellingId, setCancellingId] = useState(null);

  // "Buchung am Dienstag, 3. November um 08:00 Uhr"
  const cancelQuestion = (booking) => {
    const dateOnly = booking.Date?.split('T')[0] || booking.Date;
    const dateLabel = format(new Date(`${dateOnly}T00:00:00`), 'EEEE, d. MMMM', { locale: de });
    return `Buchung am ${dateLabel} um ${toHHMM(booking['Start Time'])} Uhr`;
  };

  const handleCancelSingle = async (booking) => {
    if (!window.confirm(`${cancelQuestion(booking)} für ${personsLabel(personsOf(booking))} stornieren?`)) return;
    const bookingId = booking.id;
    try {
      setCancellingId(bookingId);
      await bookingAPI.cancelBooking(bookingId);
      loadBookings();
    } catch (err) {
      alert(err.response?.data?.error || 'Stornierung fehlgeschlagen');
    } finally {
      setCancellingId(null);
    }
  };

  // Woche navigieren
  const goToPreviousWeek = () => setCurrentWeek((prev) => addWeeks(prev, -1));
  const goToNextWeek = () => setCurrentWeek((prev) => addWeeks(prev, 1));
  const goToCurrentWeek = () => setCurrentWeek(new Date());

  if (loading) {
    return <div className="loading">Lade deine Buchungen...</div>;
  }

  // For "past" check per day column
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  return (
    <div className="my-bookings">
      <h2>Meine Buchungen</h2>

      {error && <div className="error-banner">{error}</div>}

      {/* Wochen-Navigation */}
      <div className="week-navigation">
        <button onClick={goToPreviousWeek} className="nav-btn">← Vorherige</button>
        <div className="week-label">
          <span>
            {format(weekStart, 'd. MMM', { locale: de })} - {format(weekEnd, 'd. MMM yyyy', { locale: de })}
          </span>
          <button onClick={goToCurrentWeek} className="today-btn">Heute</button>
        </div>
        <button onClick={goToNextWeek} className="nav-btn">Nächste →</button>
      </div>

      {/* Wochenkalender Grid */}
      <div className="week-calendar">
        {weekDays.map((day) => {
          const dayBookings = getBookingsForDay(day);
          const isToday = isSameDay(day, new Date());
          const isPast = day < todayStart;

          return (
            <div
              key={day.toISOString()}
              className={`day-column ${isToday ? 'today' : ''} ${isPast ? 'past' : ''}`}
            >
              <div className="day-header">
                <span className="day-name">{format(day, 'EEE', { locale: de })}</span>
                <span className="day-number">{format(day, 'd')}</span>
              </div>

              <div className="day-bookings">
                {dayBookings.length === 0 ? (
                  <div className="no-booking">-</div>
                ) : (
                  groupBookingsByTime(dayBookings).map(([time, bookingsAtTime]) => {
                    const persons = bookingsAtTime.reduce((sum, b) => sum + personsOf(b), 0);
                    return (
                      <div key={time} className={`time-group ${persons > 1 ? 'multiple' : ''}`}>
                        <span className="group-time">{time}</span>
                        {persons > 1 && (
                          <span className="group-count" title={personsLabel(persons)}>{persons} Pers.</span>
                        )}
                        <button
                          onClick={() => handleCancelGroup(bookingsAtTime)}
                          className="cancel-group-btn"
                          title="Stornieren"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Zusammenfassung */}
      <div className="bookings-summary">
        <p>{upcomingCount === 1 ? '1 bevorstehende Buchung' : `${upcomingCount} bevorstehende Buchungen`}</p>
      </div>

      {/* Buchungsliste */}
      {upcomingBookings.length > 0 && (
        <div className="bookings-list">
          <div className="bookings-list-header">
            <span className="blist-h-date">Datum</span>
            <span className="blist-h-time">Uhrzeit</span>
            <span className="blist-h-occupancy">Gebucht für</span>
            <span className="blist-h-action"></span>
          </div>
          {upcomingBookings.map((booking) => {
            const dateOnly = booking.Date?.split('T')[0] || booking.Date;
            const time = toHHMM(booking['Start Time']);
            const dateObj = new Date(dateOnly);
            const isProcessing = cancellingId === booking.id;

            return (
              <div key={booking.id} className="booking-list-item">
                <div className="booking-list-info">
                  <span className="booking-list-day">{format(dateObj, 'EEEE', { locale: de })}</span>
                  <span className="booking-list-datestr">{format(dateObj, 'd. MMMM yyyy', { locale: de })}</span>
                </div>
                <div className="booking-list-time">{time} Uhr</div>
                <span className="booking-list-occupancy">
                  für {personsLabel(personsOf(booking))}
                </span>
                <button
                  onClick={() => handleCancelSingle(booking)}
                  className="booking-list-cancel"
                  disabled={isProcessing}
                >
                  {isProcessing ? '...' : 'Stornieren'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyBookings;